import {
    api,
    apiErrorMessage,
    applyApiErrors,
    oneSpec,
    useAuthStore,
    useZodForm,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    AssistantButton,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    Field,
    FieldGrid,
    Input,
    PageHeader,
    PublicId,
    ReasonDialog,
    SearchableSelect,
    StatusBadge,
    UnsavedGuard,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import type {
    Merchant,
    MerchantReview,
    StaffOnboardingApplication,
    StaffSubscriptionSettingsUpdateRequest,
    SubscriptionSettings,
} from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { EntityTags } from '@/components/entity-tags';
import { merchantActions } from '@/lib/merchant-actions';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { ApplicationReview, isReviewable } from '@/pages/merchants/application';
import {
    OrganizationBlock,
    StoresBlock,
} from '@/pages/merchants/organization-stores';
import {
    MerchantPricingCard,
    useMerchantPricing,
} from '@/pages/pricing/merchant-pricing';
import { MerchantRiskCard } from '@/pages/risk/merchant-risk-card';
import {
    MerchantScreeningNotice,
    useMerchantSanctionsCase,
} from '@/pages/risk/screening';

type Action = 'approve' | 'reject' | 'suspend' | 'close';

const COPY: Record<
    Action,
    {
        title: string;
        label: string;
        description: string;
        destructive?: boolean;
        required: boolean;
    }
> = {
    approve: {
        title: 'Approve this merchant',
        label: 'Approve',
        description:
            'The merchant can take live payments once this is granted. When the tenant policy asks for a second person, the request appears in Approvals and expires if nobody decides.',
        required: false,
    },
    reject: {
        title: 'Reject this application',
        label: 'Reject application',
        description:
            'The merchant is closed, its keys are revoked and it is emailed your reason. The person can register a new business later.',
        destructive: true,
        required: true,
    },
    suspend: {
        title: 'Suspend this merchant',
        label: 'Suspend',
        description:
            'Checkout stops taking orders; the balance stays. The reason goes to the merchant and the audit log.',
        destructive: true,
        required: true,
    },
    close: {
        title: 'Close this merchant',
        label: 'Close',
        description:
            'Starts the offboarding: the services erase their rows, the case tracks the progress.',
        destructive: true,
        required: true,
    },
};

/** The reads of `/merchants/:id`: the page runs them and the route prefetches them (BM-46 §2.0). */
export const merchantReads = (id: string | undefined) => ({
    merchant: oneSpec(`/staff/merchants/${id}`),
    // BM-59: the review needs the application with its documents and history
    application: oneSpec(`/staff/merchants/${id}/application`),
    subscriptionSettings: oneSpec(
        `/staff/merchants/${id}/subscription-settings`,
    ),
});

const BILLING_MODES = [
    { value: 'bemor_managed', label: 'Bemor-managed' },
    { value: 'merchant_managed', label: 'Merchant-managed' },
];
const SENDERS = [
    { value: 'bemor', label: 'Bemor' },
    { value: 'merchant', label: 'Merchant' },
];
const SENDER_FIELDS = [
    ['payment_failed_sender', 'Payment-failed sender'],
    ['card_expiring_sender', 'Card-expiring sender'],
] as const;
// the ranges are the API's (BM-56 § 1); shown as hints, checked by the staff PATCH alone
const LEAD_DAYS = [
    ['trial_ending_lead_days', 'Trial-ending lead days', '7 to 21 days'],
    ['price_increase_lead_days', 'Price-increase lead days', '14 to 30 days'],
    ['annual_renewal_lead_days', 'Annual-renewal lead days', '15 to 45 days'],
] as const;

const labelOf = (options: { value: string; label: string }[], value: string) =>
    options.find((o) => o.value === value)?.label ?? value;

// strings as typed: the service validates every value and answers 422 per field (no client rule, BM-56 VALIDATORS)
const settingsSchema = z.object({
    billing_mode: z.string(),
    default_retry_waits_hours: z.string(),
    grace_period_days: z.string(),
    trial_ending_lead_days: z.string(),
    price_increase_lead_days: z.string(),
    annual_renewal_lead_days: z.string(),
    payment_failed_sender: z.string(),
    card_expiring_sender: z.string(),
});
type SettingsForm = z.infer<typeof settingsSchema>;

/** The PATCH body: only the fields the staff member changed, typed back to the contract's shapes. */
function changedSettings(
    values: SettingsForm,
    dirty: Partial<Record<keyof SettingsForm, boolean>>,
): StaffSubscriptionSettingsUpdateRequest {
    const body: Record<string, unknown> = {};
    for (const key of Object.keys(values) as (keyof SettingsForm)[]) {
        if (!dirty[key]) continue;
        const value = values[key].trim();
        if (key === 'default_retry_waits_hours')
            body[key] =
                value === ''
                    ? null
                    : value.split(',').map((hours) => Number(hours.trim()));
        // a cleared day field is null so the API refuses it: Number('') is 0, a valid grace (bm-111 § 15)
        else if (key.endsWith('_days'))
            body[key] = value === '' ? null : Number(value);
        else body[key] = value;
    }
    return body as StaffSubscriptionSettingsUpdateRequest;
}

/** BM-56 § 8: the merchant's subscription settings; holders of billing.merchant_settings.manage edit them. */
function SubscriptionSettingsCard({
    path,
    query,
}: {
    path: string;
    query: ReturnType<typeof useOne<SubscriptionSettings>>;
}) {
    const can = useAuthStore((s) => s.can);
    const [editing, setEditing] = useState(false);
    const settings = query.data?.data;
    return (
        <Card data-testid="subscription-settings">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium">
                    Subscription settings
                </CardTitle>
                {settings && !editing && can(P.merchantSettingsManage) ? (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </Button>
                ) : null}
            </CardHeader>
            <CardContent>
                {query.isError ? (
                    <p role="alert" className="text-destructive text-sm">
                        {apiErrorMessage(query.error)}
                    </p>
                ) : !settings ? (
                    <p className="text-muted-foreground text-sm">Loading…</p>
                ) : editing ? (
                    <SubscriptionSettingsForm
                        path={path}
                        settings={settings}
                        onDone={() => setEditing(false)}
                    />
                ) : (
                    <FieldGrid>
                        <DetailRow
                            label="Billing mode"
                            value={labelOf(
                                BILLING_MODES,
                                settings.billing_mode,
                            )}
                            layout="stacked"
                        />
                        <DetailRow
                            label="Retry waits"
                            value={
                                settings.default_retry_waits_hours
                                    ? `${settings.default_retry_waits_hours.join(', ')} hours`
                                    : 'Platform default'
                            }
                            layout="stacked"
                        />
                        <DetailRow
                            label="Grace period"
                            value={`${settings.grace_period_days} days`}
                            layout="stacked"
                        />
                        {LEAD_DAYS.map(([key, label]) => (
                            <DetailRow
                                key={key}
                                label={label}
                                value={`${settings[key]} days`}
                                layout="stacked"
                            />
                        ))}
                        {SENDER_FIELDS.map(([key, label]) => (
                            <DetailRow
                                key={key}
                                label={label}
                                value={labelOf(SENDERS, settings[key])}
                                layout="stacked"
                            />
                        ))}
                    </FieldGrid>
                )}
            </CardContent>
        </Card>
    );
}

function SubscriptionSettingsForm({
    path,
    settings,
    onDone,
}: {
    path: string;
    settings: SubscriptionSettings;
    onDone: () => void;
}) {
    const [error, setError] = useState<string | null>(null);
    const form = useZodForm(settingsSchema, {
        defaultValues: {
            billing_mode: settings.billing_mode,
            default_retry_waits_hours:
                settings.default_retry_waits_hours?.join(', ') ?? '',
            grace_period_days: String(settings.grace_period_days),
            trial_ending_lead_days: String(settings.trial_ending_lead_days),
            price_increase_lead_days: String(settings.price_increase_lead_days),
            annual_renewal_lead_days: String(settings.annual_renewal_lead_days),
            payment_failed_sender: settings.payment_failed_sender,
            card_expiring_sender: settings.card_expiring_sender,
        },
    });
    const save = useWrite(
        async (body: StaffSubscriptionSettingsUpdateRequest) =>
            (await api().patch<SubscriptionSettings>(path, body)).data,
        [path],
    );
    const errors = form.formState.errors;
    const select = (
        name: 'billing_mode' | (typeof SENDER_FIELDS)[number][0],
        label: string,
        options: { value: string; label: string }[],
    ) => (
        <Field key={name} id={name} label={label} error={errors[name]?.message}>
            <SearchableSelect
                id={name}
                label={label}
                options={options}
                value={form.watch(name)}
                clearable={false}
                invalid={Boolean(errors[name])}
                onChange={(v) =>
                    v ? form.setValue(name, v, { shouldDirty: true }) : null
                }
            />
        </Field>
    );
    return (
        <form
            method="post"
            className="space-y-4"
            onSubmit={form.handleSubmit((values) => {
                setError(null);
                const body = changedSettings(
                    values,
                    form.formState.dirtyFields,
                );
                if (Object.keys(body).length === 0) return onDone();
                save.mutate(body, {
                    onSuccess: onDone,
                    onError: (e) => {
                        if (!applyApiErrors(form, e))
                            setError(apiErrorMessage(e));
                    },
                });
            })}
        >
            <UnsavedGuard dirty={form.formState.isDirty} />
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {select('billing_mode', 'Billing mode', BILLING_MODES)}
                <Field
                    id="default_retry_waits_hours"
                    label="Retry waits (hours)"
                    hint="Hours after each failure, 1 to 10 waits totalling at most 504 (21 days), comma-separated; empty for the platform default"
                    error={errors.default_retry_waits_hours?.message}
                >
                    <Input
                        id="default_retry_waits_hours"
                        aria-invalid={
                            Boolean(errors.default_retry_waits_hours) ||
                            undefined
                        }
                        {...form.register('default_retry_waits_hours')}
                    />
                </Field>
                <Field
                    id="grace_period_days"
                    label="Grace period (days)"
                    hint="0 to 7 days after the last retry before the subscription is cancelled"
                    error={errors.grace_period_days?.message}
                >
                    <Input
                        id="grace_period_days"
                        aria-invalid={
                            Boolean(errors.grace_period_days) || undefined
                        }
                        {...form.register('grace_period_days')}
                    />
                </Field>
                {LEAD_DAYS.map(([key, label, hint]) => (
                    <Field
                        key={key}
                        id={key}
                        label={label}
                        hint={hint}
                        error={errors[key]?.message}
                    >
                        <Input
                            id={key}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            aria-invalid={Boolean(errors[key]) || undefined}
                            {...form.register(key)}
                        />
                    </Field>
                ))}
                {SENDER_FIELDS.map(([key, label]) =>
                    select(key, label, SENDERS),
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    type="submit"
                    className="max-sm:w-full"
                    disabled={save.isPending}
                >
                    Save
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="max-sm:w-full"
                    onClick={onDone}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

export function MerchantPage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const reads = merchantReads(id);
    const query = useOne<Merchant>(reads.merchant.path);
    const application = useOne<StaffOnboardingApplication>(
        reads.application.path,
    );
    // read beside the merchant, not inside its card: the route prefetches both (BM-46 §2.0)
    const settings = useOne<SubscriptionSettings>(
        reads.subscriptionSettings.path,
    );
    // BM-62: the merchant's pricing, beside the merchant for the same reason
    const pricing = useMerchantPricing(id);
    const [action, setAction] = useState<Action | null>(null);
    const [requestingInfo, setRequestingInfo] = useState(false);
    const invalidate = [
        '/staff/merchants',
        '/staff/queues',
        '/staff/approvals',
    ];
    const act = useWrite(
        async (body: { action: Action; reason: string }) =>
            (
                await api().post<Merchant>(
                    `/staff/merchants/${id}/${body.action}`,
                    { reason: body.reason },
                )
            ).data,
        invalidate,
    );
    const startReview = useWrite(
        async () =>
            (
                await api().post<MerchantReview>(
                    `/staff/merchants/${id}/review`,
                    { state: 'in_review' },
                )
            ).data,
        invalidate,
    );
    // BM-59: the acquirer reviewer reads the merchant (and downloads documents) but never decides. A staff
    // member who may suspend (risk) is not that reviewer: the lifecycle buttons keep their own permission.
    const readOnly =
        !can(P.onboardingReview) &&
        !can(P.merchantsApprove) &&
        !can(P.merchantsSuspend);
    const app = application.data?.data;
    const reviewable = app ? isReviewable(app.state) : false;
    // BM-125 decision 38: an open sanctions case on the owners or the business name blocks the onboarding approval
    const sanctionsCase = useMerchantSanctionsCase(id);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Merchants', href: '/merchants' }]}
                {...detailTitle(
                    query,
                    query.data?.data.display_name ||
                        query.data?.data.legal_name,
                    id,
                )}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                        {app?.open_queue_item?.kind === 're_review' ? (
                            <span
                                className="text-warning-fg text-xs"
                                data-testid="re-review-note"
                            >
                                Resubmitted, re-review open
                            </span>
                        ) : null}
                    </>
                }
                primaryAction={
                    <AssistantButton
                        surface="merchant"
                        subject={{ type: 'merchant', id }}
                    />
                }
            />
            <DetailBody query={query} title="Merchant">
                {(m) => {
                    const actions = merchantActions(m.status);
                    // BM-85 decision 25: an approved merchant's address change waits here; Approve accepts it and
                    // Reject puts the previous address back (the merchant stays approved either way).
                    const addressReview =
                        m.status === 'approved' && Boolean(m.address_review);
                    // BM-65 decision 21: an approved merchant's new payout bank account reopens the review the same way;
                    // Approve verifies it, Reject fails it with the reason (the merchant stays approved either way).
                    const bankReview =
                        m.status === 'approved' &&
                        !addressReview &&
                        app?.state === 'in_review';
                    const copy = (a: Action) =>
                        bankReview && a === 'approve'
                            ? {
                                  ...COPY.approve,
                                  title: 'Approve the bank account',
                                  description:
                                      'The payout bank account is verified and payouts can go to it.',
                              }
                            : bankReview && a === 'reject'
                              ? {
                                    ...COPY.reject,
                                    title: 'Reject the bank account',
                                    label: 'Reject bank account',
                                    description:
                                        'The account is marked failed with your reason; the merchant stays approved, sees the reason on the dashboard and enters another one.',
                                }
                              : addressReview && a === 'approve'
                                ? {
                                      ...COPY.approve,
                                      title: 'Approve the new address',
                                      description:
                                          'The address change is accepted: the merchant is verified again and payouts resume.',
                                  }
                                : addressReview && a === 'reject'
                                  ? {
                                        ...COPY.reject,
                                        title: 'Reject the address change',
                                        label: 'Reject address change',
                                        description:
                                            'The previous address comes back; the merchant stays approved and is emailed your reason.',
                                    }
                                  : COPY[a];
                    return (
                        <div className="space-y-4">
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-base">
                                        {m.legal_name}
                                    </CardTitle>
                                    <div
                                        className="flex flex-wrap gap-2"
                                        data-testid="merchant-actions"
                                    >
                                        {!readOnly &&
                                        can(P.onboardingReview) &&
                                        app?.state === 'submitted' ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={startReview.isPending}
                                                onClick={() =>
                                                    startReview.mutate(
                                                        undefined,
                                                    )
                                                }
                                            >
                                                Start review
                                            </Button>
                                        ) : null}
                                        {!readOnly &&
                                        can(P.onboardingReview) &&
                                        reviewable ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setRequestingInfo(true)
                                                }
                                            >
                                                Request info
                                            </Button>
                                        ) : null}
                                        {!readOnly &&
                                        can(P.merchantsApprove) &&
                                        (actions.approve ||
                                            addressReview ||
                                            bankReview) ? (
                                            <Button
                                                size="sm"
                                                disabled={
                                                    sanctionsCase !== null &&
                                                    m.status === 'pending'
                                                }
                                                onClick={() =>
                                                    setAction('approve')
                                                }
                                            >
                                                {sanctionsCase !== null &&
                                                m.status === 'pending'
                                                    ? 'Sanctions case open'
                                                    : 'Approve'}
                                            </Button>
                                        ) : null}
                                        {!readOnly &&
                                        can(P.merchantsApprove) &&
                                        reviewable ? (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() =>
                                                    setAction('reject')
                                                }
                                            >
                                                {addressReview
                                                    ? 'Reject address change'
                                                    : bankReview
                                                      ? 'Reject bank account'
                                                      : 'Reject application'}
                                            </Button>
                                        ) : null}
                                        {can(P.merchantsSuspend) &&
                                        actions.suspend ? (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() =>
                                                    setAction('suspend')
                                                }
                                            >
                                                Suspend
                                            </Button>
                                        ) : null}
                                        {can(P.merchantsSuspend) &&
                                        actions.close ? (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() =>
                                                    setAction('close')
                                                }
                                            >
                                                Close
                                            </Button>
                                        ) : null}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {startReview.error ? (
                                        <p
                                            role="alert"
                                            className="text-destructive mb-3 text-sm"
                                        >
                                            The review could not be started;
                                            reload and try again.
                                        </p>
                                    ) : null}
                                    <FieldGrid>
                                        <DetailRow
                                            label="Status"
                                            layout="stacked"
                                        >
                                            <StatusBadge
                                                status={m.status}
                                                domain="merchant"
                                            />
                                            {m.status === 'closed' &&
                                            m.closed_reason ===
                                                'onboarding_rejected' ? (
                                                <span className="text-muted-foreground ml-2 text-xs">
                                                    application rejected
                                                </span>
                                            ) : null}
                                        </DetailRow>
                                        <DetailRow label="KYC" layout="stacked">
                                            <StatusBadge
                                                status={m.kyc_status}
                                                domain="kyc"
                                            />
                                        </DetailRow>
                                        <DetailRow
                                            label="Id"
                                            value={m.id}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Support email"
                                            value={m.support_email}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Timezone"
                                            value={m.timezone}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Refund policy version"
                                            value={String(
                                                m.refund_policy_version,
                                            )}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Created"
                                            layout="stacked"
                                        >
                                            <DateTime
                                                zone="inline"
                                                value={m.created_at}
                                                empty="—"
                                            />
                                        </DetailRow>
                                        <DetailRow
                                            label="Tags"
                                            layout="stacked"
                                        >
                                            <EntityTags
                                                entity={{
                                                    type: 'merchant',
                                                    id: m.id,
                                                }}
                                                tags={m.tags ?? []}
                                                canEdit={can(P.merchantUpdate)}
                                                canCreate={can(P.casesWrite)}
                                            />
                                        </DetailRow>
                                    </FieldGrid>
                                    <div className="mt-4 flex flex-wrap gap-3 text-xs">
                                        <Link
                                            to={`/audit?merchant_id=${m.id}`}
                                            className="underline"
                                        >
                                            Audit log
                                        </Link>
                                        <Link
                                            to={`/cases?merchant_id=${m.id}`}
                                            className="underline"
                                        >
                                            Cases
                                        </Link>
                                        <Link
                                            to={`/approvals?merchant_id=${m.id}`}
                                            className="underline"
                                        >
                                            Approvals
                                        </Link>
                                        <Link
                                            to={`/disputes?merchant_id=${m.id}`}
                                            className="underline"
                                        >
                                            Disputes
                                        </Link>
                                        {/* bm-111 § 2: the payment providers card lives on its own sub-page */}
                                        {can(P.systemsManage) ? (
                                            <Link
                                                to={`/merchants/${m.id}/payment-systems`}
                                                className="underline"
                                            >
                                                Payment systems
                                            </Link>
                                        ) : null}
                                    </div>
                                </CardContent>
                            </Card>
                            {app ? (
                                <ApplicationReview
                                    merchantId={m.id}
                                    app={app}
                                    readOnly={readOnly}
                                    requestingInfo={requestingInfo}
                                    onRequestingInfo={setRequestingInfo}
                                />
                            ) : application.isError ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-sm"
                                >
                                    The application could not be loaded.
                                </p>
                            ) : (
                                <p
                                    className="text-muted-foreground text-sm"
                                    data-testid="application-loading"
                                >
                                    Loading the application…
                                </p>
                            )}
                            <MerchantPricingCard
                                merchantId={m.id}
                                query={pricing}
                            />
                            {sanctionsCase ? (
                                <MerchantScreeningNotice
                                    sanctionsCase={sanctionsCase}
                                    pending={m.status === 'pending'}
                                />
                            ) : null}
                            {/* BM-125 decisions 1 and 2: the chargeback and fraud ratios against the networks' 1% line */}
                            <MerchantRiskCard merchantId={m.id} />
                            <SubscriptionSettingsCard
                                path={reads.subscriptionSettings.path}
                                query={settings}
                            />
                            {action ? (
                                <ReasonDialog
                                    open
                                    onOpenChange={(o) =>
                                        o ? null : setAction(null)
                                    }
                                    title={copy(action).title}
                                    description={copy(action).description}
                                    confirmLabel={copy(action).label}
                                    destructive={copy(action).destructive}
                                    required={copy(action).required}
                                    busy={act.isPending}
                                    error={act.error}
                                    onConfirm={(reason) =>
                                        act.mutate(
                                            { action, reason },
                                            {
                                                onSuccess: () =>
                                                    setAction(null),
                                            },
                                        )
                                    }
                                />
                            ) : null}
                        </div>
                    );
                }}
            </DetailBody>
            {/* BM-86 (design 0.8.0): the organization above the merchant and its stores, read-only; the page has one
                column, so the organization block sits here after the account and the stores table after it */}
            <div className="mt-4 space-y-4">
                <OrganizationBlock merchantId={id} />
                <StoresBlock merchantId={id} />
            </div>
            <div className="mt-4">
                <Comments subjectType="merchant" subjectId={id} />
            </div>
            <div className="mt-4">
                <ActivityCard entity="merchant" id={id} />
            </div>
        </div>
    );
}
